import { useShopifyAppContext, shopifyQueryKey } from '../../ShopifyAppContext'
/**
 * What: Renders the Shopify embedded-app surface for creating and managing storefront micro-surveys.
 * Transport / data-flow: Calls `/api/micro-surveys` through App Bridge-authenticated fetch and validates drafts with shared survey contracts.
 *
 */
import { useQuery } from '@tanstack/react-query'
import { useShopifyTrackedMutation } from '../../hooks/useShopifyTrackedMutation'
import { shopifyUiTracker, trackShopifyAction, trackShopifyRefetch } from '../../shopifyUiTracking'
import { useEffect, useState } from 'react'
import { shopifyApiFetch } from '../../shopifyApiFetch'
import {
  buildMicroSurveySaveBody,
  createEmptyMicroSurveyDraft,
  validateMicroSurveyDraft,
  type MicroSurveyDraft,
  type MicroSurveySaveBody,
} from './microSurveyDraft'
import { MicroSurveyEditor } from './MicroSurveyEditor'
import { MicroSurveyList } from './MicroSurveyList'
import type { MicroSurveysResponse, SavedMicroSurvey } from './types'
import { shopifyComponentCopy } from '@testfeed/product-language'
import { describeErrorForDiagnostics, resolveMerchantErrorMessage } from '../../merchantErrors'
import { reportClientDiagnostic } from '../../clientDiagnostics'
import { shopifyQueryPolicies } from '../../queries/shopifyQueryPolicies'
import { FooterHelp, HelpModal } from '../shared/PageHelp'

const HELP_MODAL = 'micro-surveys-help-modal'

type ShopifyTokenSource = Parameters<typeof shopifyApiFetch>[0]

type MicroSurveysPageProps = {
  shopify: ShopifyTokenSource
}

function surveyCountLabel(count: number) {
  return count === 1 ? '1 saved survey' : `${count} saved surveys`
}

async function readJsonError(response: Response, fallback: string) {
  const payload = (await response.json().catch((error) => {
    console.error(error)
    return {}
  })) as { error?: unknown }

  return typeof payload.error === 'string' ? payload.error : fallback
}

async function loadMicroSurveys(shopify: ShopifyTokenSource): Promise<SavedMicroSurvey[]> {
  const response = await shopifyApiFetch(shopify, '/api/micro-surveys?status=all')
  if (!response.ok) {
    throw new Error(await readJsonError(response, `Micro-surveys request failed (${response.status})`))
  }

  const payload = (await response.json()) as MicroSurveysResponse
  return Array.isArray(payload.microSurveys) ? payload.microSurveys : []
}

async function saveMicroSurvey(shopify: ShopifyTokenSource, body: MicroSurveySaveBody): Promise<SavedMicroSurvey> {
  const response = await shopifyApiFetch(shopify, '/api/micro-surveys', {
    init: {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
  })
  if (!response.ok) {
    throw new Error(await readJsonError(response, `Micro-survey save failed (${response.status})`))
  }

  return response.json() as Promise<SavedMicroSurvey>
}

async function archiveMicroSurvey(shopify: ShopifyTokenSource, surveyId: string): Promise<SavedMicroSurvey> {
  const response = await shopifyApiFetch(shopify, `/api/micro-surveys/${encodeURIComponent(surveyId)}/archive`, {
    init: {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    },
  })
  if (!response.ok) {
    throw new Error(await readJsonError(response, `Micro-survey archive failed (${response.status})`))
  }

  return response.json() as Promise<SavedMicroSurvey>
}

/** Coordinates merchant micro-survey authoring with the existing Shopify admin lifecycle routes. */
export function MicroSurveysPage({ shopify }: MicroSurveysPageProps) {
  const { shopDomain, orgId, connectionId } = useShopifyAppContext()
  const [editorDraft, setEditorDraft] = useState<MicroSurveyDraft | null>(null)
  const [draftErrors, setDraftErrors] = useState<string[]>([])
  const [saveNotice, setSaveNotice] = useState<string | null>(null)
  const [archiveError, setArchiveError] = useState<string | null>(null)
  const [archivingSurveyId, setArchivingSurveyId] = useState<string | null>(null)
  const microSurveysQuery = useQuery({
    queryKey: shopifyQueryKey(shopDomain, orgId, connectionId, ['shopify-micro-surveys']),
    queryFn: () => loadMicroSurveys(shopify),
    enabled: !!shopDomain && !!orgId && !!connectionId,
    ...shopifyQueryPolicies.microSurveys,
  })
  const saveMutation = useShopifyTrackedMutation(shopify, 'save-survey', {
    mutationFn: (body: MicroSurveySaveBody) => saveMicroSurvey(shopify, body),
    onSuccess: (saved) => {
      setEditorDraft(null)
      setDraftErrors([])
      setSaveNotice(`${saved.surveyId} saved.`)
      void microSurveysQuery.refetch()
    },
    onError: (error) => {
      console.error('Micro-survey save failed', describeErrorForDiagnostics(error))
      const message = resolveMerchantErrorMessage(
        error,
        shopifyComponentCopy.shopifyApiErrors.couldNotSaveMicroSurvey009,
      )
      setDraftErrors([message])
      reportClientDiagnostic(shopify, { schema: 'shopify-micro-surveys', paths: ['save_failed'] })
    },
  })
  const archiveMutation = useShopifyTrackedMutation(shopify, 'archive-survey', {
    mutationFn: (survey: SavedMicroSurvey) => {
      setArchivingSurveyId(survey.surveyId)
      return archiveMicroSurvey(shopify, survey.surveyId)
    },
    onSuccess: (archived) => {
      setArchiveError(null)
      setSaveNotice(`${archived.surveyId} archived.`)
      void microSurveysQuery.refetch()
    },
    onError: (error) => {
      console.error('Micro-survey archive failed', describeErrorForDiagnostics(error))
      const message = resolveMerchantErrorMessage(
        error,
        shopifyComponentCopy.shopifyApiErrors.couldNotArchiveMicroSurvey010,
      )
      setArchiveError(message)
      reportClientDiagnostic(shopify, { schema: 'shopify-micro-surveys', paths: ['archive_failed'] })
    },
    onSettled: () => {
      setArchivingSurveyId(null)
    },
  })

  const microSurveys = microSurveysQuery.data ?? []
  const microSurveysQueryError = microSurveysQuery.error
  // Effect, not render body: reports once per settled list failure instead of every render.
  useEffect(() => {
    if (!microSurveysQueryError) return
    console.error('Micro-surveys list failed to load', describeErrorForDiagnostics(microSurveysQueryError))
    reportClientDiagnostic(shopify, { schema: 'shopify-micro-surveys', paths: ['list_failed'] })
  }, [microSurveysQueryError, shopify])
  const listError = microSurveysQueryError
    ? resolveMerchantErrorMessage(microSurveysQueryError, shopifyComponentCopy.shopifyApiErrors.couldNotLoad004)
    : null
  const summary = surveyCountLabel(microSurveys.length)
  const openNewEditor = () => {
    setSaveNotice(null)
    setArchiveError(null)
    setDraftErrors([])
    setEditorDraft(createEmptyMicroSurveyDraft())
  }
  const cancelEditor = () => {
    setDraftErrors([])
    setEditorDraft(null)
  }
  const saveEditorDraft = () => {
    if (!editorDraft) return
    const errors = validateMicroSurveyDraft(editorDraft)
    setDraftErrors(errors)
    if (errors.length > 0) {
      shopifyUiTracker(shopify).blocked('save-survey', 'validation')
      return
    }
    saveMutation.mutate(buildMicroSurveySaveBody(editorDraft))
  }
  const refetchMicroSurveys = () => {
    void trackShopifyRefetch(shopify, () => microSurveysQuery.refetch())
  }
  const archiveSurvey = (survey: SavedMicroSurvey) => {
    setSaveNotice(null)
    setArchiveError(null)
    archiveMutation.mutate(survey)
  }
  const editor = editorDraft ? (
    <MicroSurveyEditor
      draft={editorDraft}
      errors={draftErrors}
      isSaving={saveMutation.isPending}
      onChange={setEditorDraft}
      onCancel={() => trackShopifyAction(shopify, 'cancel-edit', cancelEditor)}
      onSave={saveEditorDraft}
    />
  ) : null
  const notice = saveNotice ? (
    <s-banner heading={shopifyComponentCopy.microSurveysMicroSurveysPage.microSurveyUpdated001} tone="success">
      <s-text>{saveNotice}</s-text>
    </s-banner>
  ) : null
  const archiveFailure = archiveError ? (
    <s-banner heading={shopifyComponentCopy.microSurveysMicroSurveysPage.microSurveyCouldNotBe002} tone="critical">
      <s-text>{archiveError}</s-text>
    </s-banner>
  ) : null

  return (
    <s-stack direction="block" gap="large">
      <s-stack direction="block" gap="base">
        <s-stack direction="inline" justifyContent="space-between" alignItems="center" gap="base">
          <s-stack direction="block" gap="small-100">
            <s-heading>{shopifyComponentCopy.microSurveysMicroSurveysPage.microSurveys003}</s-heading>
            <s-text color="subdued">{summary}</s-text>
          </s-stack>
          <s-stack direction="inline" gap="small-200">
            <s-button onClick={() => trackShopifyAction(shopify, 'create-survey', openNewEditor)} variant="primary">
              {shopifyComponentCopy.microSurveysMicroSurveysPage.newMicroSurvey004}
            </s-button>
            <s-button onClick={refetchMicroSurveys} loading={microSurveysQuery.isFetching}>
              {shopifyComponentCopy.microSurveysMicroSurveysPage.refresh005}
            </s-button>
          </s-stack>
        </s-stack>
        {notice}
        {archiveFailure}
        {editor}
        <MicroSurveyList
          microSurveys={microSurveys}
          isLoading={microSurveysQuery.isLoading}
          error={listError}
          archivingSurveyId={archivingSurveyId}
          onRetry={refetchMicroSurveys}
          onArchive={archiveSurvey}
        />
      </s-stack>
      <FooterHelp modalId={HELP_MODAL} link="how micro-surveys work" />
      <HelpModal id={HELP_MODAL} heading={shopifyComponentCopy.microSurveysMicroSurveysPage.helpModalHeading}>
        <s-paragraph>{shopifyComponentCopy.microSurveysMicroSurveysPage.helpModalBody1}</s-paragraph>
        <s-paragraph color="subdued">{shopifyComponentCopy.microSurveysMicroSurveysPage.helpModalBody2}</s-paragraph>
      </HelpModal>
    </s-stack>
  )
}
